/**
 * The Light and Shadow screen.
 *
 * One component tree, no router: the game is a single grid plus its controls,
 * and everything it needs — the puzzle, the marks, the clock — lives in this
 * component and is mirrored to localStorage on every change.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';

import {
  analyseBoard,
  applyMarks,
  cycleMark,
  impliedEdges,
  nextHint,
  startingMarks,
  type BoardStatus,
  type Change,
  type Hint,
} from './game';
import { Generator } from './generatorClient';
import {
  DIFFICULTIES,
  DIFFICULTY_LABEL,
  LIGHT,
  MAX_REGION,
  SHADOW,
  UNKNOWN,
  type Clue,
  type Difficulty,
  type Mark,
  type Puzzle,
} from './types';
import { load, save, type Stored } from './storage';
import { DEFAULT_PALETTE, PRESETS, applyPalette, presetFor, type Palette } from './theme';

const UNDO_LIMIT = 200;

// ── Tool icons ──────────────────────────────────────────────────────────────

/**
 * The three board tools, drawn rather than labelled.
 *
 * One stroke weight, one 24-unit box, no fills: they sit inside identical
 * squircles at a size where any interior detail would turn to mud.
 */
function Icon({ children }: { children: JSX.Element | JSX.Element[] }): JSX.Element {
  return (
    <svg class="ls-icon" viewBox="0 0 24 24" aria-hidden="true">
      {children}
    </svg>
  );
}

function HintIcon(): JSX.Element {
  return (
    <Icon>
      <path d="M12 3.2a5.2 5.2 0 0 0-3 9.4c.6.5 1 1.1 1 1.9v.3h4v-.3c0-.8.4-1.4 1-1.9a5.2 5.2 0 0 0-3-9.4Z" />
      <path d="M9.8 17.4h4.4" />
      <path d="M10.6 20.3h2.8" />
    </Icon>
  );
}

function UndoIcon(): JSX.Element {
  return (
    <Icon>
      <path d="M4.5 9.2h9a5.3 5.3 0 0 1 0 10.6H8.2" />
      <path d="M8 4.7 3.6 9.2 8 13.7" />
    </Icon>
  );
}

/** A finished block with its border drawn in: what "fill" does, as a picture. */
function EdgeIcon(): JSX.Element {
  return (
    <Icon>
      <rect x="9" y="9" width="6" height="6" rx="1" />
      <path d="M9 5.5h6M9 18.5h6M5.5 9v6M18.5 9v6" />
    </Icon>
  );
}

/** A circle filled on one side: the two shades, and the button that sets them. */
function ColoursIcon(): JSX.Element {
  return (
    <Icon>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M12 3.6a8.4 8.4 0 0 1 0 16.8Z" fill="currentColor" stroke="none" />
    </Icon>
  );
}

function TrashIcon(): JSX.Element {
  return (
    <Icon>
      <path d="M4.5 6.8h15" />
      <path d="M9.5 6.8V4.6h5v2.2" />
      <path d="m6.8 6.8 1 12.6h8.4l1-12.6" />
      <path d="M10.4 10.4v5.6M13.6 10.4v5.6" />
    </Icon>
  );
}

interface ToolProps {
  label: string;
  /** What the button does, for anyone who cannot see the icon. */
  description: string;
  disabled: boolean;
  onClick: () => void;
  children: JSX.Element;
}

function Tool({ label, description, disabled, onClick, children }: ToolProps): JSX.Element {
  return (
    <button
      type="button"
      class="ls-tool"
      onClick={onClick}
      disabled={disabled}
      title={description}
      aria-label={description}
    >
      <span class="ls-tool-face" aria-hidden="true">
        {children}
      </span>
      {/* Visible, not a tooltip: an icon nobody can name is a button nobody
          presses, and there is room for a word under each. */}
      <span class="ls-tool-name">{label}</span>
    </button>
  );
}

// ── Board ───────────────────────────────────────────────────────────────────

const SHADE_NAME: Record<Mark, string> = {
  [UNKNOWN]: 'undecided',
  [LIGHT]: 'light',
  [SHADOW]: 'shadow',
};

interface BoardProps {
  puzzle: Puzzle;
  marks: Mark[];
  errors: ReadonlySet<number>;
  satisfied: ReadonlySet<number>;
  highlight: ReadonlySet<number>;
  frozen: boolean;
  /** Finished: the frame keeps a hairline of light. See `.ls-solved` in the CSS. */
  solved: boolean;
  /** Finished *here*, a moment ago, rather than restored already finished. */
  celebrating: boolean;
  onApply: (changes: Change[]) => void;
}

function Board(props: BoardProps): JSX.Element {
  const { puzzle, marks, errors, satisfied, highlight, frozen, solved, celebrating, onApply } =
    props;
  const numbers = useMemo(() => {
    const table = new Map<number, { clue: Clue; index: number }>();
    puzzle.clues.forEach((clue, index) => table.set(clue.cell, { clue, index }));
    return table;
  }, [puzzle.clues]);

  // A tap cycles the cell; a drag paints whatever that first tap would have
  // produced. Which one it was is only known at the end, so the origin cell and
  // the shade it decided are held until the pointer either moves to another cell
  // or lifts where it started.
  const origin = useRef<number | null>(null);
  const painting = useRef<Mark>(UNKNOWN);
  const dragged = useRef(false);
  const painted = useRef(new Set<number>());

  const paint = useCallback(
    (cell: number): void => {
      if (painted.current.has(cell) || numbers.has(cell)) return;
      painted.current.add(cell);
      onApply([{ cell, mark: painting.current }]);
    },
    [numbers, onApply],
  );

  const cellAt = (event: PointerEvent): number | null => {
    const element = document.elementFromPoint(event.clientX, event.clientY);
    const raw = element?.closest('[data-cell]')?.getAttribute('data-cell');
    return raw === null || raw === undefined ? null : Number(raw);
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (frozen || event.button !== 0) return;
    const cell = cellAt(event);
    // A gesture that starts on a number has nothing to say: the number's own
    // square is given, and there is no first tap to repeat across a drag.
    if (cell === null || numbers.has(cell)) return;
    origin.current = cell;
    painting.current = cycleMark(marks[cell] as Mark);
    dragged.current = false;
    painted.current.clear();
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (frozen || origin.current === null) return;
    const cell = cellAt(event);
    if (cell === null) return;
    if (!dragged.current) {
      if (cell === origin.current) return;
      // Only now is it a drag, so the cell it started on takes the shade too.
      dragged.current = true;
      paint(origin.current);
    }
    paint(cell);
  };

  const endPointer = (): void => {
    if (origin.current !== null && !dragged.current) {
      onApply([{ cell: origin.current, mark: painting.current }]);
    }
    origin.current = null;
    dragged.current = false;
    painted.current.clear();
  };

  const onKeyDown = (event: KeyboardEvent, cell: number): void => {
    if (frozen) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!numbers.has(cell)) onApply([{ cell, mark: cycleMark(marks[cell] as Mark) }]);
      return;
    }
    const moves: Record<string, number> = {
      ArrowUp: -puzzle.width,
      ArrowDown: puzzle.width,
      ArrowLeft: -1,
      ArrowRight: 1,
    };
    const step = moves[event.key];
    if (step === undefined) return;
    const target = cell + step;
    if (target < 0 || target >= marks.length) return;
    // Left and right must not wrap onto the next row.
    if (
      Math.abs(step) === 1 &&
      Math.floor(target / puzzle.width) !== Math.floor(cell / puzzle.width)
    ) {
      return;
    }
    event.preventDefault();
    document.querySelector<HTMLElement>(`[data-cell="${target}"]`)?.focus();
  };

  return (
    <div
      class={`ls-board${solved ? ' ls-solved' : ''}${celebrating ? ' ls-just-solved' : ''}`}
      style={`--ls-cols:${puzzle.width};--ls-rows:${puzzle.height}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onPointerLeave={endPointer}
      role="grid"
      aria-label={`${puzzle.width} by ${puzzle.height} light and shadow grid`}
    >
      {marks.map((mark, cell) => {
        const number = numbers.get(cell);
        const shade = mark === LIGHT ? ' ls-light' : mark === SHADOW ? ' ls-shadow' : '';
        const given = number ? ' ls-given' : '';
        const done = number && satisfied.has(number.index) ? ' ls-done' : '';
        const wrong = errors.has(cell) ? ' ls-error' : '';
        const looking = highlight.has(cell) ? ' ls-look' : '';
        // Which diagonal the square is on. The stylesheet turns it into a delay,
        // so the finish sweeps across the board from the top-left corner instead
        // of every square lighting up at once; it costs nothing while playing.
        const wave = Math.floor(cell / puzzle.width) + (cell % puzzle.width);
        return (
          <div
            key={cell}
            data-cell={cell}
            class={`ls-cell${shade}${given}${done}${wrong}${looking}`}
            style={`--ls-wave:${wave}`}
            role="gridcell"
            tabIndex={0}
            aria-label={`Row ${Math.floor(cell / puzzle.width) + 1}, column ${
              (cell % puzzle.width) + 1
            }: ${
              number
                ? number.clue.value === null
                  ? 'number hidden, '
                  : `number ${number.clue.value}, `
                : ''
            }${SHADE_NAME[mark]}`}
            onKeyDown={(event: KeyboardEvent) => onKeyDown(event, cell)}
          >
            {/* Set in an SVG box rather than as text in the cell, which is what
                makes it scale: the board is between five and eight squares
                across depending on the tier, and a font size that suited one
                would be wrong on the others. */}
            {number ? (
              <svg class="ls-number" viewBox="0 0 24 24" aria-hidden="true">
                <text x="12" y="12">
                  {number.clue.value ?? '?'}
                </text>
              </svg>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ── Dialogs ─────────────────────────────────────────────────────────────────

/**
 * A real `<dialog>` opened with `showModal`, rather than an overlay of our own:
 * the backdrop, the focus trap and Escape all come with it.
 */
function Modal({
  onClose,
  children,
}: {
  onClose: () => void;
  children: JSX.Element | JSX.Element[];
}): JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  return (
    <dialog
      class="ls-dialog"
      ref={dialog}
      onCancel={(event: Event) => {
        // Escape: let it close through the same path as the button, so the
        // component unmounts instead of leaving a closed dialog mounted.
        event.preventDefault();
        onClose();
      }}
      onClick={(event: MouseEvent) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div class="ls-dialog-body">{children}</div>
    </dialog>
  );
}

/**
 * The one action on this screen that cannot be taken back in one press.
 *
 * Undo restores the marks a clear threw away — the history keeps them — but a
 * player who has just watched forty minutes of shading vanish does not know
 * that, and should not have to.
 */
function ConfirmClear({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  return (
    <Modal onClose={onCancel}>
      <h2>Clear the board?</h2>
      <p>Every square you have shaded goes. The numbers and the clock stay.</p>
      <div class="ls-dialog-buttons">
        <button type="button" onClick={onCancel}>
          Keep playing
        </button>
        <button type="button" class="ls-destructive" onClick={onConfirm}>
          Clear board
        </button>
      </div>
    </Modal>
  );
}

/**
 * The colours, changed while you watch.
 *
 * Every edit lands on the board behind the dialog immediately and is saved as it
 * is made: picking a colour is a thing you judge by looking at it, and a preview
 * you have to confirm is a preview of the wrong board. There is nothing to undo
 * for the same reason — the presets and the reset are one press away.
 */
function ColourSettings({
  palette,
  onChange,
  onClose,
}: {
  palette: Palette;
  onChange: (next: Palette) => void;
  onClose: () => void;
}): JSX.Element {
  const chosen = presetFor(palette);
  const rows: { key: keyof Palette; label: string }[] = [
    { key: 'light', label: 'Light squares' },
    { key: 'shadow', label: 'Shadow squares' },
    { key: 'page', label: 'Page behind the board' },
  ];

  return (
    <Modal onClose={onClose}>
      <h2>Colours</h2>
      <div class="ls-presets">
        {PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            class={`ls-preset${chosen?.id === preset.id ? ' ls-preset-on' : ''}`}
            aria-pressed={chosen?.id === preset.id}
            onClick={() => onChange({ ...preset.palette })}
          >
            <span class="ls-chips" aria-hidden="true">
              <span class="ls-chip" style={`background:${preset.palette.light}`} />
              <span class="ls-chip" style={`background:${preset.palette.shadow}`} />
              <span class="ls-chip" style={`background:${preset.palette.page}`} />
            </span>
            {preset.name}
          </button>
        ))}
      </div>

      <div class="ls-colors">
        {rows.map((row) => (
          <label key={row.key} class="ls-color">
            <span>{row.label}</span>
            <input
              type="color"
              value={palette[row.key]}
              onInput={(event) =>
                onChange({ ...palette, [row.key]: (event.target as HTMLInputElement).value })
              }
            />
          </label>
        ))}
      </div>
      <p class="ls-settings-note">
        Numbers and headings take whichever of black or white can be read on the colour behind them,
        so nothing you pick can hide them.
      </p>

      <div class="ls-dialog-buttons">
        <button type="button" onClick={() => onChange({ ...DEFAULT_PALETTE })}>
          Reset
        </button>
        <button type="button" class="ls-primary" onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}

// ── Feedback timing ─────────────────────────────────────────────────────────

const NONE: ReadonlySet<number> = new Set<number>();

/**
 * How long the board must hold a fault before it is mentioned.
 *
 * Long enough to cover a drag across a row, and long enough to cover a mis-tap.
 * Painting a run of squares passes through states that break a rule on the way
 * to one that does not — a region three cells over its number, a moment before
 * the shade beside it is corrected — and shading the wrong square takes up to two
 * more taps to get back out of, because a square goes light, then dark, then
 * blank. Complaining through either of those reads as a flicker rather than as
 * feedback about anything, so the board waits for the player to stop moving.
 */
const SETTLE_MS = 1400;

/**
 * The board's faults, once they have stopped moving.
 *
 * Only the *appearance* of a fault waits. Once something is being complained
 * about, changes and withdrawals are immediate: nothing flickers on the way to
 * saying less, and a stale error would leave a red square where the player has
 * already fixed it.
 */
function useSettledErrors(status: BoardStatus | null): ReadonlySet<number> {
  const [errors, setErrors] = useState<ReadonlySet<number>>(NONE);
  const showing = useRef(false);

  useEffect(() => {
    if (!status || status.errors.size === 0) {
      showing.current = false;
      setErrors(NONE);
      return undefined;
    }
    if (showing.current) {
      setErrors(status.errors);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      showing.current = true;
      setErrors(status.errors);
    }, SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  return errors;
}

// ── Screen ──────────────────────────────────────────────────────────────────

function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

export function App(): JSX.Element {
  const generator = useMemo(() => new Generator(), []);
  const initial = useMemo<Stored>(() => load(), []);

  const [difficulty, setDifficulty] = useState(initial.difficulty);
  const [palette, setPalette] = useState<Palette>(initial.palette);
  const [puzzle, setPuzzle] = useState<Puzzle | null>(initial.game?.puzzle ?? null);
  // Empty until the first puzzle arrives, which is the same moment the board
  // first renders — nothing reads the marks before then.
  const [marks, setMarks] = useState<Mark[]>(initial.game?.marks ?? []);
  const [elapsed, setElapsed] = useState(initial.game?.elapsed ?? 0);
  const [completed, setCompleted] = useState(initial.game?.completed ?? false);
  // Whether the board was finished in front of the player rather than restored
  // already finished. Deliberately not saved: coming back to a solved board is
  // not the same event as solving one, and the celebration belongs to the second.
  // A finished board still looks finished either way.
  const [celebrating, setCelebrating] = useState(false);
  const [best, setBest] = useState(initial.best);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [hint, setHint] = useState<Hint | null>(null);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [choosingColours, setChoosingColours] = useState(false);
  const history = useRef<Mark[][]>([]);

  const status = useMemo(() => (puzzle ? analyseBoard(puzzle, marks) : null), [puzzle, marks]);
  const errors = useSettledErrors(status);

  // ── Persistence and the clock ────────────────────────────────────────────

  useEffect(() => {
    save({
      difficulty,
      palette,
      game: puzzle ? { puzzle, marks, elapsed, completed } : null,
      best,
    });
  }, [difficulty, palette, puzzle, marks, elapsed, completed, best]);

  // The stylesheet ships the default palette, so this only has anything to do
  // once the player has chosen otherwise — but it runs on mount either way,
  // because a saved palette has to be on the board before the first paint the
  // player sees rather than after it.
  useEffect(() => {
    applyPalette(palette);
  }, [palette]);

  useEffect(() => {
    if (!puzzle || completed) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') setElapsed((value) => value + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [puzzle, completed]);

  // Finishing is detected from the board, not from a "check" button: the moment
  // the last square is shaded the puzzle is over, and the clock stops there.
  useEffect(() => {
    if (!puzzle || !status?.solved || completed) return;
    setCompleted(true);
    setCelebrating(true);
    setBest((previous) => {
      const current = previous[puzzle.difficulty];
      if (current !== undefined && current <= elapsed) return previous;
      return { ...previous, [puzzle.difficulty]: elapsed };
    });
  }, [puzzle, status?.solved, completed, elapsed]);

  // ── Generation ───────────────────────────────────────────────────────────

  const startPuzzle = useCallback(
    async (want: Difficulty): Promise<void> => {
      setBusy(true);
      setProblem(null);
      try {
        const next = await generator.request(want);
        setPuzzle(next);
        setMarks(startingMarks(next));
        setElapsed(0);
        setCompleted(false);
        setCelebrating(false);
        setHint(null);
        setConfirmingClear(false);
        history.current = [];
        generator.warm(want);
      } catch (error) {
        setProblem(
          error instanceof Error
            ? `Could not build a puzzle: ${error.message}`
            : 'Could not build a puzzle.',
        );
      } finally {
        setBusy(false);
      }
    },
    [generator],
  );

  // First visit with nothing saved: build something to play rather than showing
  // an empty frame with a button on it.
  useEffect(() => {
    if (!puzzle && !busy) void startPuzzle(difficulty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (puzzle && !busy) generator.warm(difficulty);
  }, [generator, puzzle, busy, difficulty]);

  // ── Board edits ──────────────────────────────────────────────────────────

  const apply = useCallback(
    (changes: Change[]): void => {
      if (!puzzle || changes.length === 0 || completed) return;
      // Any edit can invalidate the hint, and a highlight left over from before
      // the move points somewhere that may no longer be the place to look.
      setHint(null);
      setMarks((previous) => {
        const next = applyMarks(puzzle, previous, changes);
        if (next === previous) return previous;
        history.current.push(previous);
        if (history.current.length > UNDO_LIMIT) history.current.shift();
        return next;
      });
    },
    [puzzle, completed],
  );

  const undo = useCallback((): void => {
    const previous = history.current.pop();
    if (previous) {
      setMarks(previous);
      setCompleted(false);
      setCelebrating(false);
      setHint(null);
    }
  }, []);

  const clear = useCallback((): void => {
    if (!puzzle) return;
    history.current.push(marks);
    setMarks(startingMarks(puzzle));
    setCompleted(false);
    setCelebrating(false);
    setHint(null);
    setConfirmingClear(false);
  }, [puzzle, marks]);

  const showHint = useCallback((): void => {
    if (puzzle) setHint(nextHint(puzzle, marks));
  }, [puzzle, marks]);

  const tidy = useCallback((): void => {
    if (puzzle) apply(impliedEdges(puzzle, marks));
  }, [puzzle, marks, apply]);

  const changeDifficulty = (next: Difficulty): void => {
    setDifficulty(next);
    void startPuzzle(next);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const bestTime = puzzle ? best[puzzle.difficulty] : undefined;
  const hintCells = useMemo(() => new Set(hint?.cells ?? []), [hint]);
  const given = puzzle ? puzzle.clues.length : 0;
  const cells = puzzle ? puzzle.width * puzzle.height : 0;
  const shaded = Math.max(0, (status?.filled ?? 0) - given);
  const hiddenNumbers = puzzle ? puzzle.clues.filter((clue) => clue.value === null).length : 0;

  return (
    <main class="ls-app">
      <header class="ls-header">
        <a class="ls-back" href="/">
          ← Games
        </a>
        <h1>Light and Shadow</h1>
        <p class="ls-rule">
          Shade every square light or dark. Each block of one shade holds exactly one number, and
          that number is how many squares it has.
        </p>
      </header>

      <section class="ls-controls" aria-label="Puzzle settings">
        <label class="ls-field">
          <span>Difficulty</span>
          <select
            value={difficulty}
            disabled={busy}
            onChange={(event) =>
              changeDifficulty((event.target as HTMLSelectElement).value as Difficulty)
            }
          >
            {DIFFICULTIES.map((option) => (
              <option key={option} value={option}>
                {DIFFICULTY_LABEL[option]}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          class="ls-primary"
          disabled={busy}
          onClick={() => void startPuzzle(difficulty)}
        >
          {busy ? 'Building…' : 'New puzzle'}
        </button>
        <button
          type="button"
          class="ls-gear"
          title="Colours"
          aria-label="Colours"
          onClick={() => setChoosingColours(true)}
        >
          <ColoursIcon />
        </button>
      </section>

      {choosingColours && (
        <ColourSettings
          palette={palette}
          onChange={setPalette}
          onClose={() => setChoosingColours(false)}
        />
      )}

      {problem && (
        <p class="ls-problem" role="alert">
          {problem}
        </p>
      )}

      {busy && (
        <p class="ls-building" role="status">
          <span class="ls-spinner" aria-hidden="true" />
          Building a puzzle…
        </p>
      )}

      {puzzle && (
        <>
          <div class="ls-status">
            <span class="ls-clock">{formatTime(elapsed)}</span>
            <span class="ls-count">
              {shaded}/{cells - given} squares
            </span>
            {bestTime !== undefined && <span class="ls-best">best {formatTime(bestTime)}</span>}
          </div>

          <div class={`ls-frame${busy ? ' ls-frame-busy' : ''}`}>
            <Board
              puzzle={puzzle}
              marks={marks}
              errors={errors}
              satisfied={status?.satisfied ?? NONE}
              highlight={hintCells}
              frozen={completed || busy}
              solved={completed}
              celebrating={celebrating}
              onApply={apply}
            />
          </div>

          {/* Below the board rather than over it: the finished grid is the thing
              worth looking at when you finish, and a panel across it hides the
              shape you just closed. */}
          {completed && (
            <div class={`ls-win${celebrating ? ' ls-win-new' : ''}`} role="status">
              <strong>Solved!</strong>
              <span>
                {DIFFICULTY_LABEL[puzzle.difficulty]} · {formatTime(elapsed)}
              </span>
              <button type="button" class="ls-primary" onClick={() => void startPuzzle(difficulty)}>
                Play another
              </button>
            </div>
          )}

          {errors.size > 0 && (
            <p class="ls-warning" role="status">
              A highlighted block cannot be right — check the number it holds against the squares it
              has, and that it holds exactly one.
            </p>
          )}

          {hint && (
            <p class="ls-hint-box" role="status">
              💡 {hint.message}
            </p>
          )}

          <div class="ls-actions">
            <Tool label="Hint" description="Show a hint" disabled={completed} onClick={showHint}>
              <HintIcon />
            </Tool>
            <Tool label="Undo" description="Undo the last move" disabled={completed} onClick={undo}>
              <UndoIcon />
            </Tool>
            <Tool
              label="Fill"
              description="Shade around the blocks that are already the right size"
              disabled={completed}
              onClick={tidy}
            >
              <EdgeIcon />
            </Tool>
          </div>

          {/* Its own row, well clear of the three above, and behind a question.
              As the fourth button in that grid it was a thumb's width from undo,
              and the two are not equally survivable mistakes. */}
          <div class="ls-clear-row">
            <button
              type="button"
              class="ls-clear"
              disabled={completed || shaded === 0}
              onClick={() => setConfirmingClear(true)}
            >
              <TrashIcon />
              Clear board
            </button>
          </div>

          {confirmingClear && (
            <ConfirmClear onCancel={() => setConfirmingClear(false)} onConfirm={clear} />
          )}

          {/* Only when there is one on the board. A ? changes what a number
              tells you, so the range it stands for is a rule rather than a note,
              and a rule about nothing on screen is just noise. */}
          {hiddenNumbers > 0 && (
            <p class="ls-help ls-hidden-note">
              <strong>
                {hiddenNumbers === 1
                  ? `One of the ${given} numbers is hidden.`
                  : `${hiddenNumbers} of the ${given} numbers are hidden.`}
              </strong>{' '}
              A <b>?</b> is a number like any other — somewhere from 1 to {MAX_REGION} — and working
              out which one is part of the puzzle. Its block is finished when you have fenced it in.
            </p>
          )}

          <p class="ls-help">
            Tap once for light, again for dark, again to clear it. Drag to carry that same shade
            across a run of squares. Two blocks of one shade may never touch, so a finished block is
            always fenced in by the other.
          </p>
          <p class="ls-meta">
            {DIFFICULTY_LABEL[puzzle.difficulty]} · {puzzle.width}×{puzzle.height} · seed{' '}
            {puzzle.seed.toString(36)}
            {puzzle.difficulty !== difficulty && (
              <> · closest to {DIFFICULTY_LABEL[difficulty]} we could build</>
            )}
          </p>
        </>
      )}
    </main>
  );
}
